import { useState, useCallback } from 'react';
import { Check, FlaskConical, Beaker, Sparkles } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Distinguishing Na₂CO₃ from NaOH',
    content: `Both compounds turned pink with phenolphthalein (both are basic), but they have different anions.

**The Key Difference:**
- Na₂CO₃ contains **carbonate** (CO₃²⁻)
- NaOH contains **hydroxide** (OH⁻)

**Using the Vinegar Test:**
Adding acetic acid (vinegar) to each:

**Na₂CO₃ + 2CH₃COOH → 2NaCH₃COO + H₂O + CO₂↑**
→ BUBBLES! Carbonate produces CO₂ gas

**NaOH + CH₃COOH → NaCH₃COO + H₂O**
→ No bubbles. Simple neutralization reaction

This is the same carbonate test we used on CaCO₃, confirming that carbonate detection works regardless of the metal ion.`
  },
  2: {
    title: 'Identifying MgSO₄ with NaOH',
    content: `MgSO₄ is the only soluble compound that precipitates with NaOH.

**The Reaction:**
MgSO₄(aq) + 2NaOH(aq) → Mg(OH)₂(s)↓ + Na₂SO₄(aq)

**Why This Works:**
- Mg²⁺ ions combine with OH⁻ to form insoluble Mg(OH)₂
- The precipitate appears as a white, gelatinous solid
- Other compounds don't form insoluble hydroxides

**Precipitate Characteristics:**
- White, fluffy/gelatinous appearance
- Settles slowly
- Often described as "milk of magnesia" appearance

**Chemistry Note:**
This is a **double displacement reaction** where the less soluble product (Mg(OH)₂) precipitates out of solution.`
  },
  3: {
    title: 'Identifying NaHCO₃ (Baking Soda)',
    content: `Among the remaining neutral compounds, only NaHCO₃ reacts with acid.

**Remaining Neutral Compounds:**
- NaHCO₃ (sodium bicarbonate)
- NaCl (sodium chloride)
- Sucrose (sugar)
- H₃BO₃ (boric acid)

**The Bicarbonate Reaction:**
NaHCO₃ + CH₃COOH → NaCH₃COO + H₂O + CO₂↑

**Why NaHCO₃ Bubbles:**
The bicarbonate ion (HCO₃⁻) reacts with H⁺:
HCO₃⁻ + H⁺ → H₂CO₃ → H₂O + CO₂↑

**Baking Soda vs Baking Powder:**
- Baking soda is pure NaHCO₃
- Baking powder contains NaHCO₃ + dry acid
- Both produce CO₂ when activated (by acid or heat + moisture)`
  },
  4: {
    title: 'Identifying H₃BO₃ with Alcohol',
    content: `Boric acid has unique solubility in organic solvents.

**The Alcohol Solubility Test:**
H₃BO₃ dissolves in isopropyl alcohol, while ionic compounds do not.

**Why This Works:**
- H₃BO₃ is a **covalent compound** (molecular, not ionic)
- Covalent compounds can dissolve in organic solvents
- Ionic compounds (NaCl) require polar solvents like water

**Structure of Boric Acid:**
- Trigonal planar B(OH)₃ structure
- Weak intermolecular forces
- Can be solvated by alcohol molecules

**Remaining After This Test:**
- NaCl (ionic, won't dissolve in alcohol)
- Sucrose (covalent but too large for rapid alcohol dissolution)`
  },
  5: {
    title: 'Distinguishing NaCl from Sucrose',
    content: `The final distinction uses temperature-dependent solubility.

**Hot Water Solubility:**
| Compound | Cold Water | Hot Water |
|----------|------------|-----------|
| NaCl | 36 g/100mL | 39 g/100mL (small change) |
| Sucrose | 200 g/100mL | 490 g/100mL (large change!) |

**The Test:**
When heated, sucrose dissolves much more readily than at room temperature.

**Why the Difference?**
- **NaCl**: Ionic lattice, solubility barely affected by temperature
- **Sucrose**: Molecular solid, much more soluble when heated

**Alternative Test:**
Heating to decomposition:
- Sucrose caramelizes (turns brown, smells sweet)
- NaCl doesn't melt until 801°C

**All 10 Compounds Identified!**`
  }
};

interface TestResult {
  tested: boolean;
  result: string;
}

export default function Part6Differentiation() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [basicDiff, setBasicDiff] = useState<TestResult>({ tested: false, result: '' });
  const [mgso4Test, setMgso4Test] = useState<TestResult>({ tested: false, result: '' });
  const [nahco3Test, setNahco3Test] = useState<TestResult>({ tested: false, result: '' });
  const [h3bo3Test, setH3bo3Test] = useState<TestResult>({ tested: false, result: '' });
  const [sucroseTest, setSucroseTest] = useState<TestResult>({ tested: false, result: '' });
  const [currentStep, setCurrentStep] = useState(1);

  const completedSteps = [basicDiff, mgso4Test, nahco3Test, h3bo3Test, sucroseTest].filter(t => t.tested).length;

  const handleBasicDiff = useCallback(() => {
    if (!basicDiff.tested) {
      setBasicDiff({ tested: true, result: 'Na₂CO₃ bubbles, NaOH does not' });
      setCurrentStep(2);
    }
  }, [basicDiff.tested]);

  const handleMgso4Test = useCallback(() => {
    if (basicDiff.tested && !mgso4Test.tested) {
      setMgso4Test({ tested: true, result: 'White gelatinous precipitate - Mg(OH)₂' });
      setCurrentStep(3);
    }
  }, [basicDiff.tested, mgso4Test.tested]);

  const handleNahco3Test = useCallback(() => {
    if (mgso4Test.tested && !nahco3Test.tested) {
      setNahco3Test({ tested: true, result: 'Bubbles with vinegar - bicarbonate' });
      setCurrentStep(4);
    }
  }, [mgso4Test.tested, nahco3Test.tested]);

  const handleH3bo3Test = useCallback(() => {
    if (nahco3Test.tested && !h3bo3Test.tested) {
      setH3bo3Test({ tested: true, result: 'Dissolves in alcohol - boric acid' });
      setCurrentStep(5);
    }
  }, [nahco3Test.tested, h3bo3Test.tested]);

  const handleSucroseTest = useCallback(() => {
    if (h3bo3Test.tested && !sucroseTest.tested) {
      setSucroseTest({ tested: true, result: 'Fully dissolves when heated - sucrose' });
    }
  }, [h3bo3Test.tested, sucroseTest.tested]);

  const allComplete = sucroseTest.tested;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part VI: Differentiation Tests</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Basic Differentiation', hint: 'Distinguish Na₂CO₃ from NaOH', done: basicDiff.tested },
            { id: 2, title: 'Identify MgSO₄', hint: 'NaOH precipitation test', done: mgso4Test.tested },
            { id: 3, title: 'Identify NaHCO₃', hint: 'Vinegar test on neutrals', done: nahco3Test.tested },
            { id: 4, title: 'Identify H₃BO₃', hint: 'Alcohol solubility test', done: h3bo3Test.tested },
            { id: 5, title: 'Distinguish NaCl/Sucrose', hint: 'Hot water solubility', done: sucroseTest.tested }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <LabBench
        experimentName="Differentiation Tests"
        completedCount={completedSteps}
        totalSteps={5}
        equipment={['NaOH solution', 'Isopropyl alcohol', 'Hot water bath', 'Vinegar']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="diff-workspace">
          {/* Test 1: Basic Differentiation */}
          {currentStep === 1 && !basicDiff.tested && (
            <div className="test-card" onClick={handleBasicDiff}>
              <div className="test-header">
                <h3>Distinguish Na<sub>2</sub>CO<sub>3</sub> from NaOH</h3>
                <span className="test-number">Test 1</span>
              </div>
              <p>Both turned pink with phenolphthalein. Add vinegar to distinguish.</p>
              <div className="test-visual">
                <div className="tube-pair">
                  <div className="tube-item">
                    <svg viewBox="0 0 50 100" width={60} height={120}>
                      <rect x="12" y="10" width="26" height="70" rx="3" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="2" />
                      <rect x="14" y="30" width="22" height="45" fill="#E91E63" opacity="0.7" rx="2" />
                      <text x="25" y="90" fontSize="8" fill="#CCC" textAnchor="middle">Na₂CO₃</text>
                    </svg>
                    <span className="expected">Will BUBBLE</span>
                  </div>
                  <div className="tube-item">
                    <svg viewBox="0 0 50 100" width={60} height={120}>
                      <rect x="12" y="10" width="26" height="70" rx="3" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="2" />
                      <rect x="14" y="30" width="22" height="45" fill="#E91E63" opacity="0.7" rx="2" />
                      <text x="25" y="90" fontSize="8" fill="#CCC" textAnchor="middle">NaOH</text>
                    </svg>
                    <span className="expected">No reaction</span>
                  </div>
                </div>
              </div>
              <button className="test-btn">Add Vinegar to Both</button>
            </div>
          )}

          {/* Test 2: MgSO₄ */}
          {currentStep === 2 && basicDiff.tested && !mgso4Test.tested && (
            <div className="test-card" onClick={handleMgso4Test}>
              <div className="test-header">
                <h3>Identify MgSO<sub>4</sub></h3>
                <span className="test-number">Test 2</span>
              </div>
              <p>Add 2 drops of aqueous NaOH to MgSO<sub>4</sub> - look for white precipitate.</p>
              <div className="reaction-eq">
                MgSO<sub>4</sub>(aq) + 2NaOH(aq) → Mg(OH)<sub>2</sub>(s) + Na<sub>2</sub>SO<sub>4</sub>(aq)
              </div>
              <div className="test-visual single">
                <svg viewBox="0 0 60 110" width={80} height={140}>
                  <rect x="15" y="10" width="30" height="75" rx="3" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="2" />
                  <rect x="17" y="35" width="26" height="45" fill="#BBDEFB" opacity="0.8" rx="2" />
                  <text x="30" y="95" fontSize="8" fill="#CCC" textAnchor="middle">MgSO₄</text>
                </svg>
              </div>
              <button className="test-btn">Add 2 Drops Aqueous NaOH</button>
            </div>
          )}

          {/* Test 3: NaHCO3 */}
          {currentStep === 3 && mgso4Test.tested && !nahco3Test.tested && (
            <div className="test-card" onClick={handleNahco3Test}>
              <div className="test-header">
                <h3>Identify NaHCO<sub>3</sub></h3>
                <span className="test-number">Test 3</span>
              </div>
              <p>Among remaining neutrals, only NaHCO<sub>3</sub> will bubble with vinegar.</p>
              <div className="remaining-list">
                <span className="remaining">NaHCO<sub>3</sub></span>
                <span className="remaining">NaCl</span>
                <span className="remaining">Sucrose</span>
                <span className="remaining">H<sub>3</sub>BO<sub>3</sub></span>
              </div>
              <button className="test-btn">Add Vinegar to NaHCO<sub>3</sub></button>
            </div>
          )}

          {/* Test 4: H3BO3 */}
          {currentStep === 4 && nahco3Test.tested && !h3bo3Test.tested && (
            <div className="test-card" onClick={handleH3bo3Test}>
              <div className="test-header">
                <h3>Identify H<sub>3</sub>BO<sub>3</sub> (Boric Acid)</h3>
                <span className="test-number">Test 4</span>
              </div>
              <p>Boric acid dissolves in isopropyl alcohol; ionic compounds do not.</p>
              <div className="test-visual">
                <div className="alcohol-bottle">
                  <svg viewBox="0 0 50 90" width={60} height={100}>
                    <rect x="12" y="25" width="26" height="55" rx="3" fill="#5A5A5A" />
                    <rect x="15" y="30" width="20" height="45" fill="#F5F5F5" opacity="0.4" />
                    <rect x="17" y="10" width="16" height="18" rx="2" fill="#333" />
                    <text x="25" y="55" fontSize="5" fill="#333" textAnchor="middle">Isopropyl</text>
                    <text x="25" y="65" fontSize="5" fill="#333" textAnchor="middle">Alcohol</text>
                  </svg>
                </div>
              </div>
              <button className="test-btn">Test with Alcohol</button>
            </div>
          )}

          {/* Test 5: NaCl vs Sucrose */}
          {currentStep === 5 && h3bo3Test.tested && !sucroseTest.tested && (
            <div className="test-card" onClick={handleSucroseTest}>
              <div className="test-header">
                <h3>Distinguish NaCl from Sucrose</h3>
                <span className="test-number">Test 5</span>
              </div>
              <p>Heat both in water - sucrose dissolves much more when heated.</p>
              <div className="test-visual">
                <div className="tube-pair">
                  <div className="tube-item">
                    <svg viewBox="0 0 50 100" width={60} height={120}>
                      <rect x="12" y="10" width="26" height="70" rx="3" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="2" />
                      <rect x="14" y="40" width="22" height="35" fill="#BBDEFB" opacity="0.7" rx="2" />
                      <ellipse cx="25" cy="70" rx="8" ry="3" fill="#F5F5F5" />
                      <text x="25" y="90" fontSize="8" fill="#CCC" textAnchor="middle">NaCl</text>
                    </svg>
                    <span className="expected">Partial dissolution</span>
                  </div>
                  <div className="tube-item">
                    <svg viewBox="0 0 50 100" width={60} height={120}>
                      <rect x="12" y="10" width="26" height="70" rx="3" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="2" />
                      <rect x="14" y="40" width="22" height="35" fill="#BBDEFB" opacity="0.7" rx="2" />
                      <ellipse cx="25" cy="70" rx="8" ry="3" fill="#F5F5F5" />
                      <text x="25" y="90" fontSize="8" fill="#CCC" textAnchor="middle">Sucrose</text>
                    </svg>
                    <span className="expected">FULLY dissolves</span>
                  </div>
                </div>
              </div>
              <button className="test-btn">Heat Both in Water Bath</button>
            </div>
          )}

          {/* Results Display */}
          {allComplete && (
            <div className="final-results">
              <h3><Check size={20} /> All Compounds Identified!</h3>
              <div className="results-grid">
                <div className="result-item">
                  <span className="compound">Na<sub>2</sub>CO<sub>3</sub></span>
                  <span className="method">Soluble + Basic + Bubbles</span>
                </div>
                <div className="result-item">
                  <span className="compound">NaOH</span>
                  <span className="method">Soluble + Basic + No bubbles</span>
                </div>
                <div className="result-item">
                  <span className="compound">MgSO<sub>4</sub></span>
                  <span className="method">White ppt with NaOH</span>
                </div>
                <div className="result-item">
                  <span className="compound">NaHCO<sub>3</sub></span>
                  <span className="method">Neutral + Bubbles with acid</span>
                </div>
                <div className="result-item">
                  <span className="compound">H<sub>3</sub>BO<sub>3</sub></span>
                  <span className="method">Dissolves in alcohol</span>
                </div>
                <div className="result-item">
                  <span className="compound">Sucrose</span>
                  <span className="method">High hot water solubility</span>
                </div>
                <div className="result-item">
                  <span className="compound">NaCl</span>
                  <span className="method">Last remaining (elimination)</span>
                </div>
                <div className="result-item insoluble">
                  <span className="compound">Corn Starch</span>
                  <span className="method">Insoluble + Iodine positive</span>
                </div>
                <div className="result-item insoluble">
                  <span className="compound">CaCO<sub>3</sub></span>
                  <span className="method">Insoluble + Vinegar bubbles</span>
                </div>
                <div className="result-item insoluble">
                  <span className="compound">CaSO<sub>4</sub></span>
                  <span className="method">Insoluble + No reactions</span>
                </div>
              </div>
            </div>
          )}

          {/* Completed Test Results */}
          <div className="completed-tests">
            {basicDiff.tested && (
              <div className="completed-item">
                <Sparkles size={14} />
                <span><strong>Test 1:</strong> {basicDiff.result}</span>
              </div>
            )}
            {mgso4Test.tested && (
              <div className="completed-item">
                <Sparkles size={14} />
                <span><strong>Test 2:</strong> {mgso4Test.result}</span>
              </div>
            )}
            {nahco3Test.tested && (
              <div className="completed-item">
                <Sparkles size={14} />
                <span><strong>Test 3:</strong> {nahco3Test.result}</span>
              </div>
            )}
            {h3bo3Test.tested && (
              <div className="completed-item">
                <Sparkles size={14} />
                <span><strong>Test 4:</strong> {h3bo3Test.result}</span>
              </div>
            )}
            {sucroseTest.tested && (
              <div className="completed-item">
                <Sparkles size={14} />
                <span><strong>Test 5:</strong> {sucroseTest.result}</span>
              </div>
            )}
          </div>
        </div>

        <style>{`
          .diff-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .test-card {
            background: var(--bg-elevated);
            border: 2px solid var(--border);
            border-radius: 12px;
            padding: 24px;
            max-width: 450px;
            text-align: center;
            cursor: pointer;
            transition: all 0.2s;
          }

          .test-card:hover {
            border-color: var(--secondary);
            transform: translateY(-2px);
          }

          .test-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
          }

          .test-header h3 {
            margin: 0;
            font-size: 16px;
          }

          .test-number {
            background: var(--secondary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
          }

          .test-card p {
            margin: 0 0 16px 0;
            color: var(--text-secondary);
            font-size: 14px;
          }

          .reaction-eq {
            background: var(--bg-surface);
            padding: 10px;
            border-radius: 6px;
            font-family: monospace;
            font-size: 12px;
            margin-bottom: 16px;
          }

          .test-visual {
            margin: 20px 0;
          }

          .test-visual.single {
            display: flex;
            justify-content: center;
          }

          .tube-pair {
            display: flex;
            gap: 40px;
            justify-content: center;
          }

          .tube-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .expected {
            font-size: 11px;
            color: var(--secondary);
            font-weight: 500;
          }

          .remaining-list {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            justify-content: center;
            margin: 16px 0;
          }

          .remaining {
            background: var(--bg-surface);
            padding: 6px 12px;
            border-radius: 15px;
            font-size: 12px;
          }

          .alcohol-bottle {
            display: flex;
            justify-content: center;
          }

          .test-btn {
            background: var(--secondary);
            color: white;
            border: none;
            padding: 10px 24px;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .test-btn:hover {
            background: var(--secondary-dark);
          }

          .completed-tests {
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
            max-width: 500px;
          }

          .completed-item {
            display: flex;
            align-items: center;
            gap: 10px;
            background: rgba(0, 168, 107, 0.1);
            padding: 10px 16px;
            border-radius: 8px;
            font-size: 13px;
          }

          .completed-item svg {
            color: var(--secondary);
          }

          .final-results {
            background: var(--bg-elevated);
            border: 2px solid var(--secondary);
            border-radius: 12px;
            padding: 24px;
            width: 100%;
            max-width: 600px;
          }

          .final-results h3 {
            display: flex;
            align-items: center;
            gap: 10px;
            margin: 0 0 20px 0;
            color: var(--secondary);
            justify-content: center;
          }

          .results-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          .result-item {
            background: rgba(0, 168, 107, 0.1);
            padding: 10px 14px;
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .result-item.insoluble {
            background: rgba(245, 158, 11, 0.1);
          }

          .result-item .compound {
            font-weight: 600;
            color: var(--text-primary);
            font-size: 13px;
          }

          .result-item .method {
            font-size: 11px;
            color: var(--text-secondary);
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp1"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
